"use client"

import { useEffect, useId, useState } from "react"
import { Avatar, AvatarFallback, AvatarImage, Checkbox } from "@ui/shad"
import { Loader2, LockKeyhole } from "lucide-react"
import { toast } from "sonner"

import { useMeetingPrivacy, useUpdateMeetingPrivacy } from "@/hooks/api/meetings"
import { SimpleFormDialog } from "@/components/dialogs"

interface MeetingPrivacyDialogProps {
  meetingId: string
  open: boolean
  onOpenChange: (open: boolean) => void
}

export default function MeetingPrivacyDialog({ meetingId, open, onOpenChange }: MeetingPrivacyDialogProps) {
  const dialogFormId = useId()

  const { data: privacy, isLoading } = useMeetingPrivacy(meetingId, open)
  const updatePrivacy = useUpdateMeetingPrivacy()
  const [allowedUserIds, setAllowedUserIds] = useState<Set<string>>(new Set())

  useEffect(() => {
    if (!open || !privacy) return

    const excludedIds = new Set(privacy.excluded_user_ids)
    setAllowedUserIds(new Set(privacy.available_users.map((user) => user.id).filter((id) => !excludedIds.has(id))))
  }, [open, privacy])

  function toggleUser(userId: string) {
    if (userId === privacy?.principal_user_id) return

    setAllowedUserIds((current) => {
      const next = new Set(current)
      if (next.has(userId)) next.delete(userId)
      else next.add(userId)
      return next
    })
  }

  async function handleSave() {
    if (!privacy) return

    const excludedUserIds = privacy.available_users
      .map((user) => user.id)
      .filter((userId) => userId !== privacy.principal_user_id && !allowedUserIds.has(userId))

    await updatePrivacy.mutateAsync({ meetingId, excludedUserIds })
    toast.success(excludedUserIds.length > 0 ? "Meeting privacy updated" : "Meeting is available to everyone")
    onOpenChange(false)
  }

  return (
    <SimpleFormDialog
      title={"Meeting privacy"}
      open={open}
      onOpenChange={onOpenChange}
      action={{
        label: "Save",
        form: dialogFormId,
        disabled: !privacy || updatePrivacy.isPending,
        pending: updatePrivacy.isPending,
      }}
      cancel={{ label: "Cancel", onClick: () => onOpenChange(false), disabled: updatePrivacy.isPending }}
    >
      <form
        id={dialogFormId}
        className="space-y-2"
        onSubmit={(event) => {
          event.preventDefault()
          if (!privacy || updatePrivacy.isPending) return
          void handleSave()
        }}
      >
        <>
          <p className="text-muted-foreground text-sm">
            Choose which users can access this meeting. The book principal always has access.
          </p>
          <div className="max-h-80 overflow-y-auto rounded-md border">
            {isLoading ? (
              <div className="text-muted-foreground flex items-center justify-center gap-2 p-6 text-sm">
                <Loader2 className="animate-spin" />
                Loading users
              </div>
            ) : (
              privacy?.available_users.map((user) => {
                const isPrincipal = user.id === privacy.principal_user_id
                const checked = isPrincipal || allowedUserIds.has(user.id)

                return (
                  <label
                    key={user.id}
                    className="hover:bg-muted flex cursor-pointer items-center gap-3 border-b px-3 py-2.5 last:border-b-0"
                  >
                    <Checkbox checked={checked} disabled={isPrincipal} onCheckedChange={() => toggleUser(user.id)} />
                    <Avatar size="sm">
                      <AvatarImage src={user.profile_picture_url} alt={user.full_name} className="object-cover" />
                      <AvatarFallback>{user.initials}</AvatarFallback>
                    </Avatar>
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-medium">{user.full_name}</div>
                      <div className="text-muted-foreground truncate text-xs">{user.email}</div>
                    </div>
                    {isPrincipal && (
                      <span className="text-muted-foreground flex items-center gap-1 text-xs">
                        <LockKeyhole />
                        Principal
                      </span>
                    )}
                  </label>
                )
              })
            )}
          </div>
        </>
      </form>
    </SimpleFormDialog>
  )
}
